import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import {
  Science as ScienceIcon,
  Build as BuildIcon,
  DataUsage as DataIcon,
  Assessment as AssessmentIcon,
  Rule as RuleIcon,
} from '@mui/icons-material';

export interface ComponentTourStep {
  target: string;
  title?: string;
  content: string | React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: () => void;
  highlightPadding?: number;
}

export interface ComponentTour {
  id: string;
  name: string;
  description: string;
  component: string;
  route: string;
  steps: ComponentTourStep[];
}

// Define tours for each complex component
export const componentTours: ComponentTour[] = [
  {
    id: 'workflow-studio',
    name: 'Workflow Studio Tour',
    description: 'Learn how to create and manage clinical workflows',
    component: 'WorkflowStudio',
    route: '/studio',
    steps: [
      {
        target: 'body',
        title: 'Welcome to Workflow Studio 🎨',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              This is where you design clinical workflows visually. You can create states, 
              define transitions, and set up rules that govern how studies progress.
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip size="small" label="Visual Design" color="primary" />
              <Chip size="small" label="Drag & Drop" color="secondary" />
              <Chip size="small" label="Real-time Validation" />
            </Box>
          </Box>
        ),
        placement: 'center',
      },
      {
        target: 'body',
        title: 'Getting Started',
        content: 'Look for the toolbar buttons to create new workflows, import existing ones, or access AI-powered design features.',
        placement: 'center',
      },
      {
        target: 'body',
        title: 'Workflow Canvas Features',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              When editing a workflow, you can:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Drag to create new states</li>
              <li>Connect states to define transitions</li>
              <li>Configure state properties and conditions</li>
              <li>Validate your workflow before deployment</li>
            </ul>
          </Box>
        ),
        placement: 'center',
      },
    ],
  },
  {
    id: 'ai-workflow-designer',
    name: 'AI Workflow Designer Tour',
    description: 'Discover how AI can generate workflows from your documents',
    component: 'AIWorkflowDesigner',
    route: '/ai-designer',
    steps: [
      {
        target: 'body',
        title: 'AI-Powered Workflow Creation 🤖',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              Upload protocol documents and let AI automatically generate workflows! 
              The AI analyzes your documents and creates states, transitions, and rules.
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <ScienceIcon color="primary" />
              <Typography variant="caption" color="text.secondary">
                Powered by advanced language models
              </Typography>
            </Box>
          </Box>
        ),
        placement: 'center',
      },
      {
        target: 'body',
        title: 'Using AI Designer',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              The AI Designer workflow:
            </Typography>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Upload your protocol document (PDF, DOCX, TXT, MD)</li>
              <li>Configure analysis settings if needed</li>
              <li>Review the AI's extracted information</li>
              <li>Generate the workflow (takes 10-30 seconds)</li>
              <li>Fine-tune the generated workflow as needed</li>
            </ol>
          </Box>
        ),
        placement: 'center',
      },
    ],
  },
  {
    id: 'study-workflow-status',
    name: 'Study Status Tour',
    description: 'Understand study statuses and available actions',
    component: 'ClinicalItemWorkflowStatus',
    route: '/worklist',
    steps: [
      {
        target: 'body',
        title: 'Study Workflow Status 📊',
        content: 'Monitor and manage the progress of studies through their workflow lifecycle.',
        placement: 'center',
      },
      {
        target: 'body',
        title: 'Understanding Study Status',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              Study statuses and what they mean:
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip size="small" label="Active" color="primary" />
                <Typography variant="caption">Currently in progress</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip size="small" label="Pending" color="warning" />
                <Typography variant="caption">Awaiting action</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip size="small" label="Completed" color="success" />
                <Typography variant="caption">Successfully finished</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip size="small" label="Error" color="error" />
                <Typography variant="caption">Requires intervention</Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              You can view workflow progress, take actions, and see complete history for each study.
            </Typography>
          </Box>
        ),
        placement: 'center',
      },
    ],
  },
  {
    id: 'system-sources',
    name: 'System Sources Tour',
    description: 'Learn to configure data source connections',
    component: 'SystemSources',
    route: '/system-sources',
    steps: [
      {
        target: 'body',
        title: 'System Data Sources 🔌',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              Configure connections to external systems that feed data into your workflows.
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <DataIcon color="primary" />
              <Typography variant="caption" color="text.secondary">
                Connect EMR, PACS, RIS, and custom APIs
              </Typography>
            </Box>
          </Box>
        ),
        placement: 'center',
      },
      {
        target: 'body',
        title: 'Configuring System Sources',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              System Sources allow you to connect external systems:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>HL7 FHIR APIs</li>
              <li>DICOM (PACS)</li>
              <li>HL7 v2 Messages</li>
              <li>REST APIs</li>
              <li>Database connections</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2 }}>
              For each source, you'll configure:
              • Authentication credentials
              • Connection endpoints
              • Data field mappings
              • Test the connection before saving
            </Typography>
          </Box>
        ),
        placement: 'center',
      },
    ],
  },
  {
    id: 'advanced-rule-builder',
    name: 'Rule Builder Tour',
    description: 'Master complex rule creation',
    component: 'AdvancedRuleBuilder',
    route: '/studio',
    steps: [
      {
        target: 'body',
        title: 'Advanced Rule Builder 🔧',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              Create complex business rules that control workflow behavior. 
              Rules determine when transitions occur and what actions to take.
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <RuleIcon color="primary" />
              <Typography variant="caption" color="text.secondary">
                Visual rule composition with logic operators
              </Typography>
            </Box>
          </Box>
        ),
        placement: 'center',
      },
      {
        target: 'body',
        title: 'Building Complex Rules',
        content: (
          <Box>
            <Typography variant="body2" paragraph>
              The Rule Builder helps you create complex business logic:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Combine conditions with AND/OR/NOT operators</li>
              <li>Use fact comparisons and date/time checks</li>
              <li>Verify user roles and permissions</li>
              <li>Call external APIs for dynamic rules</li>
              <li>Test rules with sample data before deployment</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Start with templates or build from scratch. Preview rules in both visual and text format.
            </Typography>
          </Box>
        ),
        placement: 'center',
      },
    ],
  },
];

// Helper function to get tour by component name
export const getComponentTour = (componentName: string): ComponentTour | undefined => {
  return componentTours.find(tour => tour.component === componentName);
};

// Helper function to get tour by route
export const getComponentTourByRoute = (route: string): ComponentTour | undefined => {
  return componentTours.find(tour => tour.route === route);
};